import type { DailyCommonResponse, ProfileDTO } from '@/api/schema'
import { dayjs } from '@/api/utils/dayjs'
import { api } from '@/app/utils/api'
import { parseError } from '@/app/utils/error'
import { createContext } from 'preact'
import { useContext } from 'preact/hooks'
import { create, type StoreApi, type UseBoundStore } from 'zustand'

type DailyState = {
  loading: boolean
  daily: DailyCommonResponse | null
  cacheKey: string | null
  error: string[]
  reset(): void
  fetch(profile?: ProfileDTO | null): Promise<void>
}

export type DailyStore = UseBoundStore<StoreApi<DailyState>>

export const DailyStoreContext = createContext<DailyStore | null>(null)

export function useDaily<T>(selector: (state: DailyState) => T) {
  const store = useContext(DailyStoreContext)

  if (!store) {
    throw new Error('DailyProvider is required')
  }

  return store(selector)
}

export const createDailyStore = () => (
  create<DailyState>((set, get) => ({
    loading: false,
    daily: null,
    cacheKey: null,
    error: [],

    reset() {
      set({
        loading: false,
        daily: null,
        cacheKey: null,
        error: [],
      })
    },

    async fetch(profile) {
      const tz = dayjs.tz.guess()
      const date = dayjs().tz(tz).format('YYYY-MM-DD')
      const profileHash = JSON.stringify(profile)
      const cacheKey = `${date}:${tz}:${profileHash}`

      if (get().cacheKey === cacheKey && get().daily) {
        return
      }

      set({ loading: true, error: [] })

      try {
        const resp = await api.get<DailyCommonResponse>(`/daily/common?tz=${tz}`)

        set({
          daily: resp.data,
          cacheKey,
          loading: false,
          error: [],
        })
      }
      catch (e) {
        set({
          loading: false,
          error: parseError(e),
        })
      }
    },
  }))
)
